import React from 'react';
import clsx from 'clsx';
import ProfileImage from '../../Header/ProfileImage';
import { normalise } from '../../shared/access/accessModel';
import { AvatarDirectory, EMPTY_AVATAR_DIRECTORY, avatarFor } from './avatarDirectory';

/**
 * A person, in a row: their real photo, their name, and one quiet line under
 * it if the caller has something to say.
 *
 * Every admin surface that names somebody goes through this, so the picture
 * chain is decided once — the record's own picture, then the organization
 * directory, then `ProfileImage`'s Gravatar, then initials. Before this, each
 * table built its own avatar + name markup, and the admin ones passed a
 * picture field that `/admin/users` does not send, so they always drew
 * initials.
 */

export interface PersonCellProps {
  /** Any person-shaped record — read through `accessModel.normalise`. */
  person: any;
  /** Organization pictures, from `useAvatarDirectory`. */
  directory?: AvatarDirectory;
  /** Overrides the name from the record (audit rows carry only `user_name`). */
  name?: string;
  /** Muted second line — a role, a qualifier. */
  subtitle?: React.ReactNode;
  /** Tailwind size pair for the avatar. Default `h-8 w-8`. */
  size?: string;
  className?: string;
}

const PersonCell: React.FC<PersonCellProps> = ({
  person,
  directory = EMPTY_AVATAR_DIRECTORY,
  name,
  subtitle,
  size = 'h-8 w-8',
  className,
}) => {
  const identity = normalise(person);
  const displayName = name || identity.name || identity.email;

  return (
    <div className={clsx('flex min-w-0 items-center gap-3', className)}>
      <ProfileImage
        profilePicture={avatarFor(person, directory)}
        email={identity.email || null}
        initials={identity.initials}
        name={displayName}
        size={size}
      />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-fg-strong" title={displayName}>
          {displayName}
        </p>
        {subtitle && <p className="truncate text-[11px] text-fg-muted">{subtitle}</p>}
      </div>
    </div>
  );
};

export default PersonCell;
